<script setup lang="ts">
import { onUnmounted, watch } from 'vue';
import {
  gaiaToasts,
  removeGaiaToast,
  type GaiaToastItem,
  type GaiaToastType,
} from '@/composables/useGaiaToast';
import { gaiaFontSize, gaiaMotion, gaiaToast } from '../themes/tokens';
import { isOfficeAddinHost } from '@/office/utils';
import GaiaButton from '../base/GaiaButton.vue';

/**
 * Renders the `useGaiaToast()` queue as the bottom-right toast stack. Mounted
 * once in App.vue; teleports to body so no layout, transform or overflow can
 * trap it. Timers live HERE, not in the composable: a toast fired before this
 * host exists just waits in the queue, and starts aging only once visible.
 */

interface ToastTimer {
  handle: number;
  startedAt: number;
  remaining: number;
}

const timers = new Map<number, ToastTimer>();

function arm(toast: GaiaToastItem, remaining: number): void {
  timers.set(toast.id, {
    handle: window.setTimeout(() => {
      timers.delete(toast.id);
      removeGaiaToast(toast.id);
    }, remaining),
    startedAt: Date.now(),
    remaining,
  });
}

// Sticky toasts (duration 0 — loading, or an explicit override) never arm.
watch(
  () => gaiaToasts.length,
  () => {
    for (const toast of gaiaToasts) {
      if (toast.duration > 0 && !timers.has(toast.id)) arm(toast, toast.duration);
    }
    // A toast evicted or destroyed while its timer runs leaves the timer live;
    // sweep them so the timeout doesn't fire against a reused slot.
    for (const [id, timer] of timers) {
      if (!gaiaToasts.some(toast => toast.id === id)) {
        window.clearTimeout(timer.handle);
        timers.delete(id);
      }
    }
  }
);

/** Hover holds the clock — the reader decides when they're done, not the timer. */
function pause(toast: GaiaToastItem): void {
  const timer = timers.get(toast.id);
  if (!timer) return;
  window.clearTimeout(timer.handle);
  timer.remaining = Math.max(1000, timer.remaining - (Date.now() - timer.startedAt));
}

function resume(toast: GaiaToastItem): void {
  const timer = timers.get(toast.id);
  if (!timer) return;
  timers.delete(toast.id);
  arm(toast, timer.remaining);
}

function runAction(toast: GaiaToastItem): void {
  toast.action?.onClick();
  dismiss(toast);
}

function dismiss(toast: GaiaToastItem): void {
  const timer = timers.get(toast.id);
  if (timer) {
    window.clearTimeout(timer.handle);
    timers.delete(toast.id);
  }
  removeGaiaToast(toast.id);
}

onUnmounted(() => {
  for (const timer of timers.values()) window.clearTimeout(timer.handle);
  timers.clear();
});

/**
 * Pin the outgoing card to the exact slot it occupied: `leave-active` takes it
 * out of flow with position:absolute, and a box's "static position" is not
 * dependable across flex/block contexts — measured coordinates are.
 */
function onBeforeLeave(el: Element): void {
  const node = el as HTMLElement;
  const stack = node.parentElement;
  if (!stack) return;
  const nodeRect = node.getBoundingClientRect();
  const stackRect = stack.getBoundingClientRect();
  // Measured from the stack's BOTTOM edge: the host is bottom-anchored, so
  // when this card leaves the flow the stack's top edge moves while its
  // bottom stays put — the bottom is the only stable reference.
  node.style.bottom = `${stackRect.bottom - nodeRect.bottom}px`;
  node.style.width = `${nodeRect.width}px`;
}

/** An error interrupts (assertive); everything else waits its turn. */
function roleFor(type: GaiaToastType): 'alert' | 'status' {
  return type === 'error' ? 'alert' : 'status';
}

const surface = gaiaToast.surface;
const text = gaiaToast.text;
const border = gaiaToast.border;
const shadow = gaiaToast.shadow;
const dotColors = gaiaToast.dot;
const fontSize = gaiaFontSize.dense;
const moveMotion = gaiaMotion.morph;
const fadeMotion = gaiaMotion.fade;
</script>

<template>
  <teleport to="body">
    <!-- Z ladder (comments app-wide agree on it): corner task panels 1000 <
         tour invite 1590/1600 < naive modals ~2000 < tour overlay 3500 <
         toasts. 6000 is where naive's own messages sat, so toasts keep
         outranking everything, tours included. -->
    <div class="gaia-toast-host" :class="{ office: isOfficeAddinHost() }" aria-live="polite">
      <transition-group
        name="gaia-toast"
        tag="div"
        class="gaia-toast-stack"
        @before-leave="onBeforeLeave"
      >
        <div
          v-for="toast in gaiaToasts"
          :key="toast.id"
          class="gaia-toast"
          :role="roleFor(toast.type)"
          :title="toast.duration > 0 ? 'Click to dismiss' : undefined"
          @mouseenter="pause(toast)"
          @mouseleave="resume(toast)"
          @focusin="pause(toast)"
          @focusout="resume(toast)"
          @click="dismiss(toast)"
        >
          <span
            class="dot"
            :class="{ pulsing: toast.type === 'loading' }"
            :style="{ background: dotColors[toast.type] }"
          />
          <span class="content">{{ toast.content }}</span>
          <gaia-button
            v-if="toast.action"
            class="action"
            text
            size="small"
            @click.stop="runAction(toast)"
          >
            {{ toast.action.label }}
          </gaia-button>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<style scoped>
/* The host never blocks the page; each pill re-enables its own pointer
   events so hover-pause and click-dismiss work. */
.gaia-toast-host {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 6000;
  pointer-events: none;
  max-width: min(380px, calc(100vw - 32px));
}

/* Clear the office add-in's fixed bottom tab bar, like GaiaCornerStack. */
.gaia-toast-host.office {
  bottom: 60px;
}

/* Clear a wizard's sticky footer, so a toast never covers Continue. */
:global(body:has(.gaia-wizard-footer) .gaia-toast-host) {
  bottom: 76px;
}

/* Block flow on purpose, not flex: the leave animation parks the outgoing
   card with `position: absolute`, and an absolutely positioned FLEX child's
   static position is computed as if it were the sole item — it snaps to the
   container's corner instead of staying in its slot. In block flow the static
   position is simply where the card already was. Right-alignment and the gap
   therefore ride margins. */
.gaia-toast-stack {
  position: relative;
}

.gaia-toast + .gaia-toast {
  margin-top: 8px;
}

/* A light card, same surface language as banners and the task panels: white,
   hairline, floating shadow — tone rides the dot, never the surface. */
.gaia-toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  padding: 10px 14px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
  color: v-bind(text);
  font-size: v-bind(fontSize);
  line-height: 1.5;
  box-shadow: v-bind(shadow);
  cursor: pointer;
  overflow-wrap: anywhere;
}

/* Centred on the first line of a wrapped message, not the whole block. */
.dot {
  flex: none;
  width: 6px;
  height: 6px;
  margin-top: 6px;
  border-radius: 50%;
}

/* The loading pulse — same idea as GaiaStatusIndicator's running dot, redrawn
   here because that component's colors and label are tuned for white surfaces. */
.dot.pulsing {
  animation: gaia-toast-pulse 1.4s ease-in-out infinite;
}

@keyframes gaia-toast-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

.content {
  min-width: 0;
}

/* The action keeps its label whole; the message wraps instead. It takes the
   message's size and line box — naive's own (`line-height: 1`) would sit it
   above the text's first line. */
.action {
  flex: none;
  font-size: inherit;
  line-height: inherit;
}

/* Plain fades in and out; a leaver goes absolute so the cards above settle
   down into its place rather than jumping. */
.gaia-toast-enter-active {
  transition: v-bind(fadeMotion);
  transition-property: opacity;
}

/* `bottom` and `width` are pinned inline by onBeforeLeave to the card's
   measured slot; `right: 0` keeps the right alignment the auto left margin no
   longer provides once the box is absolute. */
.gaia-toast-leave-active {
  position: absolute;
  right: 0;
  transition: v-bind(fadeMotion);
  transition-property: opacity;
}

.gaia-toast-enter-from,
.gaia-toast-leave-to {
  opacity: 0;
}

.gaia-toast-move {
  transition: v-bind(moveMotion);
  transition-property: transform;
}
</style>
